import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";

export default function Apply() {
  const [funds, setFunds] = useState([]);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", country: "",
    investment_amount: 100000, accredited: false,
    fund_ids: [], suitability_notes: "", kyc_ack: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/funds").then((r) => setFunds(r.data)); }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleFund = (id) => setForm((f) => ({ ...f, fund_ids: f.fund_ids.includes(id) ? f.fund_ids.filter((x) => x !== id) : [...f.fund_ids, id] }));

  async function submit(e) {
    e.preventDefault();
    if (!form.accredited) return toast.error("Applicants must confirm accredited-investor status.");
    if (!form.kyc_ack) return toast.error("Please acknowledge KYC/AML requirements.");
    if (form.fund_ids.length === 0) return toast.error("Please select at least one fund.");
    setBusy(true);
    try {
      await api.post("/applications", form);
      setSubmitted(true);
      toast.success("Application submitted.");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  }

  if (submitted) {
    return (
      <div className="container-tight py-20 max-w-2xl">
        <div className="kicker mb-3">Received</div>
        <h1 className="text-5xl mb-6">Thank you.</h1>
        <p className="text-muted-foreground leading-relaxed">
          Your application has been recorded and forwarded to our onboarding team. You will hear from us within two business days regarding next steps, including KYC/AML document collection.
        </p>
      </div>
    );
  }

  return (
    <div className="container-tight py-16 max-w-3xl">
      <div className="kicker mb-3">Apply</div>
      <h1 className="text-5xl mb-4">Prospective Client Application.</h1>
      <p className="text-muted-foreground mb-10 leading-relaxed">
        Submission does not guarantee acceptance. All applicants are subject to Piplight Capital's KYC/AML verification and accredited-investor review.
      </p>
      <form onSubmit={submit} className="space-y-6">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Full Name" testid="apply-name">
            <input required value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className="fld" />
          </Field>
          <Field label="Email" testid="apply-email">
            <input required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="fld" />
          </Field>
          <Field label="Phone" testid="apply-phone">
            <input value={form.phone} onChange={(e) => update("phone", e.target.value)} className="fld" />
          </Field>
          <Field label="Country of Residence" testid="apply-country">
            <input value={form.country} onChange={(e) => update("country", e.target.value)} className="fld" />
          </Field>
          <Field label="Proposed Investment (USD)" testid="apply-amount">
            <input required type="number" min="10000" step="1000" value={form.investment_amount} onChange={(e) => update("investment_amount", Number(e.target.value))} className="fld font-mono" />
          </Field>
        </div>

        <div>
          <div className="kicker mb-3">Funds — tap one or both</div>
          <div className="grid md:grid-cols-2 gap-3">
            {funds.filter((x) => x.status === "active").map((f) => (
              <button type="button" key={f.id}
                data-testid={`apply-fund-${f.id}`}
                onClick={() => toggleFund(f.id)}
                className={`text-left p-4 border ${form.fund_ids.includes(f.id) ? "border-primary bg-secondary" : "border-border hover:border-primary/50"}`}>
                <div className="kicker text-[10px] mb-1">{f.code}</div>
                <div className="text-sm">{f.name}</div>
                <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{f.tagline || "Tap to include this fund in your application."}</div>
              </button>
            ))}
          </div>
          {funds.length > 0 && funds.filter((x) => x.status === "active").length === 0 && (
            <div className="text-xs text-muted-foreground mt-2">No active funds currently accepting applications.</div>
          )}
        </div>

        <Field label="Suitability & Objectives" testid="apply-suit">
          <textarea rows={3} value={form.suitability_notes} onChange={(e) => update("suitability_notes", e.target.value)} className="fld resize-none" placeholder="Briefly describe your investment objectives and prior experience with alternative investments." />
        </Field>

        <label className="flex items-start gap-3 text-sm cursor-pointer">
          <input data-testid="apply-accredited" type="checkbox" checked={form.accredited} onChange={(e) => update("accredited", e.target.checked)} className="mt-0.5" />
          <span>I confirm I qualify as an accredited investor under applicable rules of my jurisdiction.</span>
        </label>
        <label className="flex items-start gap-3 text-sm cursor-pointer">
          <input data-testid="apply-kyc" type="checkbox" checked={form.kyc_ack} onChange={(e) => update("kyc_ack", e.target.checked)} className="mt-0.5" />
          <span>I acknowledge that I will be required to provide KYC/AML documentation before onboarding is completed.</span>
        </label>

        <button data-testid="apply-submit" disabled={busy} type="submit" className="btn-gold px-8 py-4 text-sm uppercase tracking-widest disabled:opacity-50">
          {busy ? "Submitting…" : "Submit Application"}
        </button>
      </form>

      <style>{`.fld{width:100%;background:hsl(226 20% 12%);border:1px solid hsl(226 20% 14%);padding:0.75rem 1rem;font-size:.875rem;color:inherit}.fld:focus{outline:none;border-color:hsl(41 40% 54%)}`}</style>
    </div>
  );
}

function Field({ label, testid, children }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="kicker text-[10px] block mb-2">{label}</span>
      {children}
    </label>
  );
}
